<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import Slider from '$lib/components/util/Slider.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';

	export const config: SectionConfig = {
		collection: 'section_text_image_slider',
		availableFor: ['pages'],
		query: gql`
        internal_name
        id
        type
        background_media {
            ...Media
        }
        icon {
            ...Media
        }
        translations {
            languages_code {
                code
            }
            section_title
            title
            text
        }
        items {
            section_text_image_slider_item_id {
                media {
                    ...Media
                }
                translations {
                    languages_code {
                        code
                    }
                    text
					title
                }
            }
        }
        `
	};
	type Props = {
		background_media: any;
		section_title: string;
		title: string;
		icon: any;
		items: any;
		id: string;
		type: any;
		text: string;
	};
</script>

<script lang="ts">
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)

	const totalSlides = data.items.length;
</script>

{#if data.type === 'large'}
	<Section gapType="padding">
		<div slot="background" class="section-background">
			<Media {...data.background_media} fit="cover" transform="xl" />
		</div>
		<div class="content">
			<div class="grid">
				<div class="col1">
					{#if data.section_title}
						<div class="section-title">{data.section_title}</div>
					{/if}
					{#if data.title}
						<div class="title">{data.title}</div>
					{/if}
					{#if data.icon}
						<div class="icon">
							<Media {...data.icon} fit="cover" transform="l" />
						</div>
					{/if}
				</div>
				<div class="col2">
					<Slider
						name={`text-image-slider-${data.id}`}
						type="single"
						navigation={true}
						slidesPerView={1}
						pagination={true}
						centeredSlides={false}
						arrowType="arrow"
						options={{ loop: true }}
					>
						{#each data.items as { section_text_image_slider_item_id: item }, idx}
							<div class="swiper-slide">
								<div class="wrapper">
									<div class="image-wrapper">
										<Media {...item.media} fit="cover" transform="l" />
									</div>
									<div class="text-wrapper">
										<div class="overview">
											{idx + 1}/{totalSlides}
										</div>
										{#if item.text}
											<div class="text">
												<Wysiwyg text={item.text} />
											</div>
										{/if}
									</div>
								</div>
							</div>
						{/each}
					</Slider>
				</div>
			</div>
		</div>
	</Section>
{:else}
	<Section class="section-text-image-slider-medium">
		{#if data.section_title}
			<div class="section-title medium">{data.section_title}</div>
		{/if}
		<div class="slider-medium">
			<Slider
				name={`text-image-slider-medium${data.id}`}
				type="single"
				navigation={true}
				slidesPerView={1}
				pagination={true}
				centeredSlides={false}
				arrowType="arrow"
				options={{ loop: true }}
			>
				{#each data.items as { section_text_image_slider_item_id: item }, idx}
					<div class="swiper-slide">
						<div class="wrapper-medium">
							<div class="image-wrapper">
								<Media {...item.media} fit="cover" transform="l" />
							</div>
							<div class="text-wrapper-medium">
								<!-- {#if data.section_title}
									<div class="section-title">{data.section_title}</div>
								{/if} -->
								{#if item.text}
									<div class="title medium">{item.title}</div>
								{/if}
								{#if item.text}
									<div class="text medium">
										<Wysiwyg text={item.text} />
									</div>
								{/if}
							</div>
						</div>
					</div>
				{/each}
			</Slider>
		</div>
	</Section>
{/if}

<style lang="scss">
	.sectio-text-image-slider {
		position: relative;
		overflow: hidden;
	}
	.background {
		background: color(grey_02);
		position: absolute;
		z-index: -1;
		width: 100%;
		height: 60%;
		top: 0;
		inset-inline-start: 0;
		overflow: hidden;
		@include media(phone) {
			height: 100%;
		}
	}
	.section-background {
		height: 100%;
		@media (max-width: 600px) {
			background: color(yellow);
			:global(img) {
				display: none;
			}
		}
	}
	.titles-wrapper {
		padding-inline-start: v(content-gap-4xl);
		@media (max-width: 1100px) {
			padding-inline-start: 0;
		}
	}
	:global(.section-text-image-slider-medium) {
		position: relative;
		overflow: hidden;
	}
	.section-text-image-slider-medium {
		position: relative;
		overflow: hidden;
	}
	.section-title {
		@include font(h6);
		margin-bottom: v(content-gap-sm);
		&.medium {
			position: absolute;
			top: 0;
			z-index: 2;
			margin-bottom: var(--content-gap-sm);
			padding-inline-start: var(--content-gap-lg);
			width: 100%;
			display: flex;
			right: 0;
			left: 50%;
			justify-content: flex-start;
			@include media(phone) {
				inset-inline-start: v(page-padding);
				transform: none;
				padding-inline-start: 0;
			}
		}
	}

	.title {
		@include font(h2);
		max-width: 10ch;
		@include media(phone) {
			max-width: 100%;
		}
		&.medium {
			margin-top: v(content-gap);
			margin-bottom: v(content-gap-2xl);
			@include media(tablet) {
				margin-bottom: v(content-gap);
			}
		}
	}

	.content {
		background: color(white);
		padding: v(content-padding);
		border-radius: v(border-radius);
	}
	.icon {
		margin-top: v(content-gap);
		width: 120px;
		height: 120px;
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		@include media(phone) {
			grid-template-columns: repeat(1, minmax(0, 1fr));
		}
	}

	.col1 {
		padding: v(content-padding-2xl);
		display: flex;
		flex-direction: column;
		@include media(tablet) {
			padding: v(content-padding-lg);
		}
		@include media(phone) {
			padding: 0;
		}
	}
	.text {
		@include font(text);
		max-width: 500px;
		&.medium {
			margin-bottom: v(content-gap);
			@include media(phone) {
				margin-bottom: 0;
			}
		}
	}
	.overview {
		@include font(text);
		font-weight: bold;
	}
	.text-wrapper {
		display: flex;
		gap: v(content-gap-sm);
		margin-top: v(content-gap);
		margin-bottom: v(content-gap-lg);
	}
	.image-wrapper {
		height: 100%;
	}
	.col2 {
		position: relative;
		.swiper-slide {
			height: 100%;
		}
		.image-wrapper {
			width: 100%;
			height: 100%;
			position: relative;
			object-fit: cover;
			aspect-ratio: 16 / 9;
			overflow: hidden;
			object-fit: cover;
			@media (max-width: 600px) {
				aspect-ratio: 1/1;
			}

			:global(img) {
				position: absolute;
				width: 100%;
				height: 100%;
				inset-inline-start: 0;
				bottom: 0;
				top: 0;
			}
			@include media(phone) {
				margin-top: v(content-padding-lg);
			}
		}
		:global(.navigation) {
			padding-bottom: 0;
			position: absolute;
			bottom: 0;
			z-index: 2;
			@include media(phone) {
				top: 0;
				height: 20px;
				padding-bottom: v(content-padding-lg);
			}
		}
		:global(.swiper-pagination) {
			// display: none;
			// position: absolute;
			// top: calc(100% - 14rem);
			// @media (max-width: 800px) {
			// 	top: calc(100% - 12.5rem);
			// }
		}
	}

	.wrapper-medium {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: v(content-gap-2xl);
		height: 100%;
		@include media(phone) {
			grid-template-columns: repeat(1, minmax(0, 1fr));
			grid-template-rows: 250px 300px;
			gap: 0;
			.image-wrapper {
				order: 2;
			}
			// display: flex;
			// flex-direction: column-reverse;
		}
	}
	.slider-medium {
		position: relative;
		:global(.navigation) {
			padding-bottom: 0;
			position: absolute;
			bottom: 0;
			z-index: 2;
			width: 100%;
			justify-content: center;
			padding-inline-start: v(content-gap-4xl);
			@include media(phone) {
				padding-inline-start: 0;
				justify-content: flex-end;
				top: 40%;
				height: 20px;
				// top: 50%;
				// position: absolute;
				// top: calc((100vw - 2 * v(page-padding)) / 16 * 9 + v(content-padding));
				// // // width: calc(100% - 2 * v(content-padding-xs));
				// // inset-inline-start: v(content-padding-xs);
				// width: 100%;
				// height: 20px;
				// justify-content: space-between;
				// // transform: translateY(100%);
				// padding-inline-start: 0;
			}
		}
		:global(.swiper-pagination) {
			width: 48%;
			bottom: 0;

			@include media(phone) {
				width: 100%;
			}
		}
		.image-wrapper {
			width: 100%;
			height: 100%;
			position: relative;
			object-fit: cover;
			aspect-ratio: 16 / 9;
			overflow: hidden;

			:global(img) {
				position: absolute;
				width: 100%;
				height: 100%;
				inset-inline-start: 0;
				bottom: 0;
				top: 0;
			}
			@include media(phone) {
				// margin-top: v(content-padding-lg);
			}
		}
		.swiper-slide {
			height: auto;
			min-height: 100%;
		}
	}
</style>
